import { isOfflineFlagEnabled } from '@/configs/offlineFeatureFlags.js';
import offlineDB, { invalidateBranchStockCaches } from '@/indexedDB/offlineDB.js';
import { canMutateOnline } from '@/services/onlineCheckService.js';
import apiClient from '@/utils/axios.js';

const inflight = new Map();
const activeRuns = new Set();
const activeControllers = new Set();
let syncGeneration = 0;
const SYNC_PAGE_SIZE = 100;
const MAX_FULL_SYNC_PAGES = 1000;
const MAX_RECENT_ROWS = 500;
/**
 * How many list syncs may be in flight at once.
 *
 * The fan-out used to start all thirteen simultaneously, each of which then
 * paginates. Against a six-connection HTTP/1.1 origin that queues most of them
 * at the socket anyway — but the *timeout clock starts when the request is sent*,
 * not when it gets a connection, so the ones at the back of the queue could time
 * out having never been served. Those timeouts were then read as a dead network.
 * Limiting the fan-out makes the queue explicit and short instead of implicit
 * and unbounded, and keeps a cold seed inside the shared per-user rate limit.
 */
const SYNC_CONCURRENCY = 3;
/**
 * Per-table retry ladder after a failure, walked one step per consecutive
 * failure and reset by any success.
 *
 * Before this existed a failed table simply stopped: `last_error` was recorded
 * and nothing ever read it. The cache stayed stale until the operator happened
 * to log in again, switch tabs after ten minutes, or cross the 24-hour full-sync
 * boundary — which is exactly the "missing data is never fetched" report.
 */
const TABLE_RETRY_SCHEDULE_MS = [30_000, 120_000, 480_000, 1_800_000];
const TABLE_RETRY_JITTER = 0.25;
/** After this many consecutive failures a table waits for a natural trigger. */
const MAX_TABLE_FAILURES = 6;
let retryTimerId = null;
/**
 * How far past its row cap a windowed scan will look for in-window rows before
 * settling for what it found. Bounds the dormant-install case (see
 * fetchAllPages) without shortening the window on any install that is trading.
 */
const WINDOW_SCAN_MULTIPLE = 4;
/**
 * How long a cache may run on deltas alone before it is reconciled in full.
 *
 * A delta returns rows that changed; it cannot report a row that was deleted,
 * or one that aged out of a retention window. Realtime covers deletions for a
 * session that was connected when they happened, and this bound covers the rest
 * — a terminal that was off overnight converges the next morning rather than
 * carrying a phantom row until someone clears the cache by hand.
 */
const FULL_SYNC_INTERVAL_MS = 24 * 60 * 60 * 1000;
const DETAIL_TABLE_FOR = {
    branches: 'branch_details',
    suppliers: 'supplier_details',
    customers: 'customer_details',
    purchases: 'purchase_details',
    sales: 'sales_details',
    transfers: 'transfer_details',
    gate_passes: 'gate_pass_details',
    wholesaler_order_requests: 'order_request_details',
    expenses: 'expense_details',
    accounts: 'account_details',
    automotives: 'automotive_details',
    load_forms: 'load_form_details',
    delivery_persons: 'delivery_person_details',
};

/**
 * The gate each list endpoint already puts in front of itself, mirrored here.
 *
 * Every entry is an existing `abort(403)`: LoadFormController::authorizeLoadForm,
 * AutomotiveController::guard, GatePassesController::authorizeGatePass,
 * TransferController::authorizeTransferManage,
 * DeliveryPersonController::authorizeDeliveryPerson, the check at the top of
 * ExpenseController::fetchExpenseList, and
 * AccountController::authorizeAccountListAccess. The orchestrator used to fan out
 * to all thirteen tables regardless, so a cashier with no load-form access — or
 * any install with the addon switched off — paid for a 403 on every login,
 * reconnect and visibility resume, burying the failures that do mean something.
 *
 * Tables whose list endpoint aborts on nothing are deliberately absent. Gating
 * those on `SubscriptionRegistry`'s permission instead would be a wider rule than
 * the server's own, and it would stop caching branches and customers for exactly
 * the operator who reads them offline today.
 */
const SYNC_ACCESS = {
    automotives: { addon: 'automotive_management', permissions: ['manage_automotives'] },
    transfers: { permissions: ['manage_transfer'] },
    gate_passes: { permissions: ['manage_gate_passes'] },
    load_forms: { addon: 'load_form_delivery', permissions: ['manage_load_forms'] },
    delivery_persons: { permissions: ['manage_load_forms', 'manage_gate_passes'] },
    expenses: { permissions: ['manage_expenses'] },
    accounts: {
        permissions: [
            'manage_accounts',
            'manage_sales',
            'manage_purchases',
            'manage_refunds',
            'manage_expenses',
            'manage_customers',
            'manage_configurations',
        ],
    },
};

/**
 * Read one of the two localStorage mirrors the stores keep.
 *
 * Read from localStorage rather than from Pinia because auth-store already
 * imports this module, and closing that loop is what forced onlineCheckService
 * into dynamic imports. Both keys are written before any sync can run:
 * `userData` is what makes `isLoggedIn` true in the first place, and App.vue
 * awaits `configStore.fetchAddonFeatures()` ahead of `convergeOfflineCaches()`.
 */
function readStoredJson(key) {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
}

/**
 * Whether this session may call `table`'s list endpoint at all.
 *
 * A fact we never learned reads as allowed, matching the router's addon guard:
 * the server gates every endpoint anyway, so a wrong "allow" costs one 403 while
 * a wrong "deny" leaves a cache permanently unseeded.
 */
export function canSyncTable(table) {
    const required = SYNC_ACCESS[table];
    if (!required) return true;

    if (required.addon) {
        const enabled = readStoredJson('addonFeatures')?.enabled;
        if (Array.isArray(enabled) && !enabled.includes(required.addon)) return false;
    }

    if (required.permissions) {
        // Owner and admin arrive holding every permission name — see
        // User::getScopesAttribute — so the privileged bypass needs no branch.
        const scopes = readStoredJson('userData')?.scopes;
        if (Array.isArray(scopes) && !required.permissions.some((p) => scopes.includes(p))) return false;
    }

    return true;
}

function metaKey(table, scope) {
    return `${table}:${scope || 'all'}`;
}

function incomingRowChanged(existing, incoming) {
    if (!existing) return false;
    return Object.keys(incoming)
        .filter((key) => key !== '_cached_at' && key !== '_sync_scope')
        .some((key) => JSON.stringify(existing[key]) !== JSON.stringify(incoming[key]));
}

function pageEnvelope(res) {
    const body = res?.data;
    if (body && !Array.isArray(body) && body.data && !Array.isArray(body.data)) {
        return body.data;
    }
    return body;
}

/**
 * Latest `updated_at` across a set of rows, as the raw string the server sent.
 *
 * Parsed only for ordering — the original string goes back to the server, so a
 * UTC ISO list and a naive app-timezone list each round-trip unchanged.
 */
function latestWatermark(rows, previous = null) {
    let bestValue = previous;
    let bestTime = previous ? new Date(previous).getTime() : -Infinity;
    if (Number.isNaN(bestTime)) bestTime = -Infinity;

    for (const row of rows) {
        const raw = row?.updated_at;
        if (!raw) continue;
        const time = new Date(raw).getTime();
        if (Number.isNaN(time) || time <= bestTime) continue;
        bestTime = time;
        bestValue = raw;
    }

    return bestValue;
}

/**
 * Test for "inside this list's retention window", or null when there is none.
 *
 * Handed to fetchAllPages rather than applied to its result, because the row cap
 * has to be counted in *kept* rows. Filtering afterwards means the client keeps
 * the newest N by id and then throws some away, while `SubscriptionRegistry`
 * windows first and takes N from what remains — so one backdated document is
 * enough to leave the two holding different sets forever, and every checksum
 * after that reports drift on a cache nothing is actually wrong with.
 *
 * Null for a delta. A delta returns whatever changed, and dropping an aged-out
 * row from it would leave the watermark unable to advance past that row — the
 * same window would be re-requested forever. The next full reconcile prunes
 * anything that has since aged out.
 */
function retentionWindow(field, days, { updatedSince = null } = {}) {
    if (updatedSince) return null;

    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;

    return (row) => {
        const at = row?.[field] ? new Date(row[field]).getTime() : Date.now();
        return !Number.isNaN(at) && at >= cutoff;
    };
}

/**
 * Exhaust a paginated list endpoint.
 *
 * A full scan replaces the local table, and replacing a cache from page one
 * alone can silently delete every row beyond that page. An incremental scan
 * (`updatedSince`) instead walks the changed rows in watermark order, so a
 * truncation at `maxRows` is a resumable prefix rather than a hole: the caller's
 * next watermark lands exactly where this one stopped.
 *
 * `keepRow` filters *before* `maxRows` is counted, so a windowed list keeps N
 * rows it wants rather than N rows minus whatever it then discarded. Skipped
 * rows still count as progress — they were read, they were simply not wanted.
 *
 * That filtering is also why a windowed scan needs a *scanned* bound and not
 * just a kept one. A shop that has been dormant for a season has no rows inside
 * its retention window at all, and without this the pager would walk the entire
 * table to prove it. Stopping early leaves the cache holding a subset, which is
 * what it held before the filter moved inside the loop anyway — and the
 * manifest's eviction floor already refuses to prune against a subset.
 */
async function fetchAllPages(
    endpoint,
    payload = {},
    { maxRows = Infinity, maxPages = MAX_FULL_SYNC_PAGES, signal, updatedSince = null, keepRow = null } = {},
) {
    const rows = [];
    const seenIds = new Set();
    const maxScanned = keepRow && Number.isFinite(maxRows) ? maxRows * WINDOW_SCAN_MULTIPLE : Infinity;
    let scanned = 0;
    let truncated = false;

    for (let page = 1; ; page += 1) {
        if (signal?.aborted) throw new DOMException('Offline list sync cancelled.', 'AbortError');
        if (page > maxPages) {
            throw new Error(`Pagination for ${endpoint} exceeded the ${maxPages}-page safety limit.`);
        }
        const res = await apiClient.post(
            endpoint,
            {
                ...payload,
                page,
                perPage: SYNC_PAGE_SIZE,
                per_page: SYNC_PAGE_SIZE,
                ...(updatedSince ? { updatedSince } : {}),
                // The total only backs the first page's hasMore fallback; every
                // later page is settled by the endpoint's +1 probe row. Counting
                // again per page costs a COUNT(*) over the whole filtered set
                // and changes nothing.
                fetchMode: { type: 'pagination', performCount: page === 1 },
            },
            { offlinePolicy: 'read', skipProgress: true, signal },
        );

        const batch = unwrapList(res, endpoint);
        const envelope = pageEnvelope(res);
        let newIds = 0;

        for (const row of batch) {
            const id = row?.id;
            if (id == null) continue;
            const key = `${typeof id}:${String(id)}`;
            if (seenIds.has(key)) continue;
            seenIds.add(key);
            // Progress is "a new id was read", not "a row was kept" — otherwise a
            // page filtered out in full would trip the no-progress guard below.
            newIds += 1;
            scanned += 1;
            if (keepRow && !keepRow(row)) continue;
            rows.push(row);
        }

        // Both exits mean "this is a prefix, not the whole list". The caller must
        // know, because a reconcile that prunes against a partial read deletes
        // rows the server still has.
        if (rows.length >= maxRows) return { rows: rows.slice(0, maxRows), truncated: true };
        if (scanned >= maxScanned) return { rows, truncated: true };

        const total = Number(envelope?.total);
        const currentPage = Number(envelope?.current_page ?? page);
        const lastPage = Number(envelope?.last_page);
        const explicitHasMore = envelope?.hasMore;
        const hasMore =
            typeof explicitHasMore === 'boolean'
                ? explicitHasMore
                : Number.isFinite(lastPage)
                  ? currentPage < lastPage
                  : Number.isFinite(total)
                    ? rows.length < total
                    : batch.length >= SYNC_PAGE_SIZE;

        if (!hasMore) return { rows, truncated };

        // Guard against endpoints that ignore `page`; never reconcile from an
        // incomplete repeated first page.
        if (!batch.length || newIds === 0) {
            throw new Error(`Pagination for ${endpoint} made no progress on page ${page}.`);
        }
    }
}

/**
 * When a table that has failed `failures` times in a row may be tried again.
 *
 * Returns null past MAX_TABLE_FAILURES: a table failing for the seventh time is
 * not having a bad minute, and continuing to poll it turns one broken endpoint
 * into a permanent background request loop. It stays eligible for the next
 * natural trigger (login, reconnect, manual refresh) — which is the point at
 * which something has actually changed.
 */
export function nextAttemptAt(failures) {
    if (failures > MAX_TABLE_FAILURES) return null;
    const base = TABLE_RETRY_SCHEDULE_MS[Math.min(failures - 1, TABLE_RETRY_SCHEDULE_MS.length - 1)];
    const spread = base * TABLE_RETRY_JITTER;
    return new Date(Date.now() + Math.round(base - spread + Math.random() * spread * 2)).toISOString();
}

/**
 * The scope each runner syncs under, so the orchestrator can find its meta row.
 * Mirrors the `scope` each sync* function passes to syncListTable.
 */
const SYNC_SCOPES = {
    branches: 'all',
    suppliers: 'all',
    customers: 'all',
    automotives: 'all',
    delivery_persons: 'all',
    accounts: 'all',
    purchases: 'last-90-days',
    sales: 'last-90-days',
    transfers: 'last-90-days',
    gate_passes: 'last-90-days',
    load_forms: 'last-90-days',
    wholesaler_order_requests: 'last-90-days',
    expenses: 'last-90-days',
};

/**
 * Run the tasks that are due, skipping any table still inside its retry window,
 * and hold the result array in the caller's original order.
 *
 * A table in backoff resolves as `{ok:false, reason:'backoff'}` rather than
 * being dropped, so the summary still names every domain — the orchestrator's
 * per-table labelling is what makes a partial failure diagnosable.
 */
async function runDueTasks(tasks, { honourBackoff = true } = {}) {
    const due = [];
    const results = new Array(tasks.length);

    for (let i = 0; i < tasks.length; i += 1) {
        const [table, run] = tasks[i];
        if (honourBackoff) {
            const meta = await offlineDB.offline_sync_meta?.get(metaKey(table, SYNC_SCOPES[table] ?? 'all'));
            const nextAt = meta?.next_attempt_at ? Date.parse(meta.next_attempt_at) : NaN;
            if (!Number.isNaN(nextAt) && nextAt > Date.now()) {
                results[i] = {
                    status: 'fulfilled',
                    value: { ok: false, reason: 'backoff', retryAt: meta.next_attempt_at },
                };
                continue;
            }
        }
        due.push([i, run]);
    }

    const settled = await runWithLimit(
        due.map(([, run]) => run),
        SYNC_CONCURRENCY,
    );
    due.forEach(([index], position) => {
        results[index] = settled[position];
    });

    return results;
}

/** Run thunks with a bounded number in flight; never rejects (mirrors allSettled). */
async function runWithLimit(thunks, limit) {
    const results = new Array(thunks.length);
    let cursor = 0;

    const worker = async () => {
        for (;;) {
            const index = cursor++;
            if (index >= thunks.length) return;
            try {
                results[index] = { status: 'fulfilled', value: await thunks[index]() };
            } catch (reason) {
                results[index] = { status: 'rejected', reason };
            }
        }
    };

    await Promise.all(Array.from({ length: Math.min(limit, thunks.length) }, worker));
    return results;
}

async function recordMeta(table, scope, patch) {
    const key = metaKey(table, scope);
    const existing = (await offlineDB.offline_sync_meta?.get(key)) || { key, table, scope: scope || 'all' };
    await offlineDB.offline_sync_meta.put({
        ...existing,
        ...patch,
        key,
        table,
        scope: scope || 'all',
    });
}

/**
 * Generic list sync, in one of two modes.
 *
 * **Incremental** (the normal case) asks the endpoint for rows changed at or
 * after the cache's watermark and upserts them. Nothing already cached is
 * touched, which is the whole point: the app keeps serving what it has, and a
 * resume costs the changes rather than the table.
 *
 * **Full** re-reads the list and reconciles — upsert, then delete every cached
 * row in scope the server did not return. It is the only mode that can observe
 * a deletion or a retention expiry, so it runs on the first sync of a table and
 * whenever the last one is older than FULL_SYNC_INTERVAL_MS, plus on request.
 *
 * A delta never prunes. Treating "absent from this response" as "deleted" would
 * empty the entire cache on the first quiet minute.
 */
export async function syncListTable({ table, scope = 'all', fetchRows, normalizeRow, scopeFilter, retentionLabel = 'all', mode = 'auto' }) {
    if (!canMutateOnline()) {
        return { ok: false, reason: 'offline' };
    }
    // Ahead of every caller rather than in the orchestrator, because the change
    // feed reaches these same runners through offlineRealtimeService: an
    // `invalidate` for a table this session may not read would otherwise 403 on
    // its own schedule, long after the seed was skipped.
    if (!canSyncTable(table)) {
        return { ok: false, reason: 'not_permitted' };
    }
    if (!offlineDB[table]) {
        return { ok: false, reason: 'missing_store' };
    }

    const lockKey = metaKey(table, scope);
    const running = inflight.get(lockKey);
    if (running) {
        // A caller that demanded a full reconcile may not be handed an
        // in-flight delta and told it succeeded: a reseed exists precisely to
        // observe deletions, which a delta structurally cannot see. Chain a real
        // full run behind the delta instead.
        if (mode !== 'full' || running.mode === 'full') {
            return running.promise;
        }
        return running.promise
            .catch(() => null)
            .then(() => syncListTable({ table, scope, fetchRows, normalizeRow, scopeFilter, retentionLabel, mode }));
    }

    const generation = syncGeneration;
    const controller = new AbortController();
    activeControllers.add(controller);
    const run = (async () => {
        const started = new Date().toISOString();
        try {
            const previous = (await offlineDB.offline_sync_meta?.get(lockKey)) || {};
            const lastFullAt = previous.last_full_sync_at ? Date.parse(previous.last_full_sync_at) : NaN;
            const fullIsDue = mode === 'full' || !previous.watermark || Number.isNaN(lastFullAt) || Date.now() - lastFullAt >= FULL_SYNC_INTERVAL_MS;
            const updatedSince = fullIsDue ? null : previous.watermark;

            await recordMeta(table, scope, { last_start_at: started, last_error: null });
            if (generation !== syncGeneration || controller.signal.aborted) {
                return { ok: false, reason: 'cancelled' };
            }
            const fetched = await fetchRows(controller.signal, updatedSince);
            // fetchAllPages reports whether it read the whole list or stopped at
            // a cap; a plain array (a caller that cannot truncate) means it did.
            const rawRows = Array.isArray(fetched) ? fetched : (fetched?.rows ?? []);
            const truncated = Array.isArray(fetched) ? false : Boolean(fetched?.truncated);
            if (generation !== syncGeneration) {
                return { ok: false, reason: 'cancelled' };
            }
            // Read off the server payload, before normalizers coerce anything.
            const watermark = latestWatermark(rawRows || [], previous.watermark ?? null);
            const rows = (rawRows || [])
                .map((r) => (normalizeRow ? normalizeRow(r) : r))
                .filter((r) => r && r.id != null)
                .map((r) => ({
                    ...r,
                    _cached_at: new Date().toISOString(),
                    _sync_scope: scope,
                }));

            const detailTableName = DETAIL_TABLE_FOR[table];
            const detailTable = detailTableName ? offlineDB[detailTableName] : null;
            const transactionTables = [offlineDB[table], offlineDB.offline_sync_meta];
            if (detailTable) transactionTables.push(detailTable);

            await offlineDB.transaction('rw', transactionTables, async () => {
                if (generation !== syncGeneration || controller.signal.aborted) {
                    throw new DOMException('Offline list sync cancelled.', 'AbortError');
                }
                // A delta only needs the rows it is about to overwrite, so it
                // reads those by key instead of walking the whole store.
                const existing = updatedSince
                    ? (await offlineDB[table].bulkGet(rows.map((r) => r.id))).filter(Boolean)
                    : scopeFilter
                      ? await offlineDB[table].filter(scopeFilter).toArray()
                      : await offlineDB[table].toArray();
                if (generation !== syncGeneration || controller.signal.aborted) {
                    throw new DOMException('Offline list sync cancelled.', 'AbortError');
                }
                const existingById = new Map(existing.map((row) => [String(row.id), row]));

                if (rows.length) {
                    await offlineDB[table].bulkPut(rows);
                }
                if (generation !== syncGeneration || controller.signal.aborted) {
                    throw new DOMException('Offline list sync cancelled.', 'AbortError');
                }

                // Only a *complete* full read knows what the server no longer
                // has. In a delta, "not in this response" means "did not
                // change"; in a truncated scan it means "I stopped reading
                // before I got there". Pruning on either deletes live rows.
                const incomingIds = new Set(rows.map((r) => String(r.id)));
                const mayPrune = !updatedSince && !truncated;
                const staleIds = mayPrune ? existing.filter((r) => !incomingIds.has(String(r.id))).map((r) => r.id) : [];
                if (staleIds.length) {
                    await offlineDB[table].bulkDelete(staleIds);
                }
                if (detailTable) {
                    const changedIds = rows.filter((row) => incomingRowChanged(existingById.get(String(row.id)), row)).map((row) => row.id);
                    const detailIds = [...new Set([...staleIds, ...changedIds])];
                    if (detailIds.length) await detailTable.bulkDelete(detailIds);
                }

                if (generation !== syncGeneration || controller.signal.aborted) {
                    throw new DOMException('Offline list sync cancelled.', 'AbortError');
                }
                await recordMeta(table, scope, {
                    last_success_at: new Date().toISOString(),
                    last_start_at: started,
                    // row_count is the store's size, not this response's; a
                    // delta of three rows does not mean the cache holds three.
                    row_count: updatedSince ? (previous.row_count ?? 0) + rows.filter((r) => !existingById.has(String(r.id))).length : rows.length,
                    retention: retentionLabel,
                    watermark: watermark ?? null,
                    // A scan that stopped early did not reconcile, so it may not
                    // claim it did — otherwise the next twenty-four hours run on
                    // deltas from a read that never saw the whole list.
                    last_full_sync_at: mayPrune ? new Date().toISOString() : (previous.last_full_sync_at ?? null),
                    truncated,
                    last_error: null,
                    last_error_kind: null,
                    consecutive_failures: 0,
                    next_attempt_at: null,
                });
            });

            return { ok: true, count: rows.length, mode: updatedSince ? 'delta' : 'full' };
        } catch (err) {
            if (generation !== syncGeneration || controller.signal.aborted || err?.name === 'AbortError') {
                return { ok: false, reason: 'cancelled' };
            }
            console.error('[offlineSync] syncListTable failed', table, scope, err);
            if (generation === syncGeneration) {
                try {
                    const previousMeta = (await offlineDB.offline_sync_meta?.get(lockKey)) || {};
                    const failures = Number(previousMeta.consecutive_failures ?? 0) + 1;
                    await recordMeta(table, scope, {
                        last_error: String(err?.message || err),
                        last_error_kind: err?.kind ?? null,
                        last_start_at: started,
                        consecutive_failures: failures,
                        // Arms the retry ladder. Nothing used to read `last_error`
                        // at all, so a table that failed once stayed stale until
                        // an unrelated event happened to run the whole fan-out.
                        next_attempt_at: nextAttemptAt(failures),
                    });
                } catch (metaError) {
                    console.warn('[offlineSync] could not record sync error metadata', table, scope, metaError);
                }
            }
            return { ok: false, reason: 'error', error: err };
        } finally {
            activeControllers.delete(controller);
            activeRuns.delete(run);
            if (inflight.get(lockKey)?.promise === run) inflight.delete(lockKey);
        }
    })();

    inflight.set(lockKey, { promise: run, mode });
    activeRuns.add(run);
    return run;
}

/**
 * Pull the row array out of a list response, or refuse to guess.
 *
 * Returning `[]` for a body it could not parse is what let one server-side fault
 * empty a cache. `CustomerController::fetchCustomers` answered its catch with
 * HTTP 200 and `{status:'error'}`; this read that as zero customers, and a full
 * reconcile — which is entitled to treat "absent from the response" as "deleted"
 * — dutifully pruned every cached customer on every terminal. An empty *list* is
 * still an array, so throwing when there is no array at all cannot misfire on a
 * genuinely empty result; it only rejects an answer that is not a list.
 */
function unwrapList(res, endpoint = 'list endpoint') {
    const body = res?.data;
    if (Array.isArray(body)) return body;
    if (Array.isArray(body?.data)) return body.data;
    if (Array.isArray(body?.data?.data)) return body.data.data;

    if (body && typeof body === 'object' && body.status === 'error') {
        throw new Error(`${endpoint} returned an error envelope: ${body.message ?? 'unknown error'}`);
    }
    throw new Error(`${endpoint} returned no recognisable row array; refusing to reconcile against it.`);
}

export async function syncBranches(options = {}) {
    return syncListTable({
        ...options,
        table: 'branches',
        scope: 'all',
        retentionLabel: 'all',
        fetchRows: (signal, updatedSince) =>
            fetchAllPages(
                'branch/table-data',
                {
                    search: '',
                    filters: [],
                    sortField: 'id',
                    sortDirection: 'asc',
                },
                { signal, updatedSince },
            ),
        normalizeRow: (r) => ({
            id: r.id,
            name: r.name ?? '',
            status: r.status ?? null,
            city: r.city ?? r.City ?? null,
            country: r.country ?? r.Country ?? null,
            phone: r.phone ?? null,
            email: r.email ?? null,
            address: r.address ?? null,
            updated_at: r.updated_at ?? null,
        }),
    });
}

export async function syncSuppliers(options = {}) {
    return syncListTable({
        ...options,
        table: 'suppliers',
        scope: 'all',
        retentionLabel: 'all',
        fetchRows: (signal, updatedSince) =>
            fetchAllPages(
                'supplier/fetchSupplierData',
                {
                    search: '',
                    filters: [],
                    sortField: 'id',
                    sortDirection: 'asc',
                },
                { signal, updatedSince },
            ),
        normalizeRow: (r) => ({
            id: r.id,
            name: r.name ?? '',
            email: r.email ?? null,
            phone: r.phone ?? null,
            company_name: r.company_name ?? r.companyName ?? null,
            status: r.status ?? null,
            updated_at: r.updated_at ?? null,
        }),
    });
}

/**
 * Full master list, not a recent-window slice.
 *
 * An asset register is not a document stream: the POS picker has to resolve any
 * customer's automotives offline, and the Archived tab reads the same store, so
 * `state: 'all'` is sent to include archived rows the active list filters out.
 */
export async function syncAutomotives(options = {}) {
    return syncListTable({
        ...options,
        table: 'automotives',
        scope: 'all',
        retentionLabel: 'all',
        // Both endpoints: the Archived tab reads this same store offline, so a
        // seed containing only active rows would leave it permanently empty.
        // Sequential rather than Promise.all: this runner is already one slot of
        // the bounded fan-out, and firing two paginated scans from inside it
        // doubled its own share of the connection pool.
        fetchRows: async (signal, updatedSince) => {
            const params = { search: '', filters: [], sortField: 'id', sortDirection: 'asc' };
            const active = await fetchAllPages('automotive/fetchAutomotives', params, { signal, updatedSince });
            const archived = await fetchAllPages('automotive/fetchArchivedAutomotives', params, {
                signal,
                updatedSince,
            });
            // Either half stopping short makes the union a partial read, and a
            // partial read may not prune.
            return {
                rows: [...active.rows, ...archived.rows],
                truncated: active.truncated || archived.truncated,
            };
        },
        normalizeRow: (r) => ({
            id: r.id,
            // Dexie is type-strict: a string here makes the row invisible to
            // .where('customer_id').equals(Number) and the picker silently empties.
            customer_id: r.customer_id != null ? Number(r.customer_id) : null,
            customer_name: r.customer_name ?? null,
            customer_type: r.customer_type ?? null,
            registration_number: r.registration_number ?? null,
            serial_number: r.serial_number ?? null,
            display_name: r.display_name ?? r.registration_number ?? r.name ?? null,
            name: r.name ?? null,
            category: r.category ?? null,
            manufacturer: r.manufacturer ?? null,
            model: r.model ?? null,
            model_year: r.model_year ?? null,
            odometer: r.odometer ?? null,
            odometer_unit: r.odometer_unit ?? 'km',
            tracks_distance: Boolean(r.tracks_distance),
            is_archived: Number(r.is_archived ?? 0),
            service_status: r.service_status ?? 'not_tracked',
            expiry_status: r.expiry_status ?? 'none',
            next_service_due_at: r.next_service_due_at ?? null,
            // Tri-state — not coerced with Number(), because null means "use
            // the site-wide rule" and Number(null) is 0, which is the exclude
            // list. See the customers normalizer for the same trap.
            service_reminders_enabled: r.service_reminders_enabled ?? null,
            next_expiry_at: r.next_expiry_at ?? null,
            last_service_at: r.last_service_at ?? null,
            lifetime_spend: r.lifetime_spend ?? null,
            sales_count: r.sales_count ?? 0,
            open_recommendations_count: r.open_recommendations_count ?? 0,
            updated_at: r.updated_at ?? null,
        }),
    });
}

/**
 * Normal customers and wholesalers are one list, so they are one sync: no
 * scopeFilter, because every row the endpoint returns is in scope.
 *
 * The tinyint flags are cached as 1/0 rather than true/false so the offline
 * DataTable filters — which compare stringified row values — match the same
 * values the server filters on. App\Http\Controllers\CustomerController's
 * realtime payload casts them the same way.
 */
export async function syncCustomers(options = {}) {
    return syncListTable({
        ...options,
        table: 'customers',
        scope: 'all',
        retentionLabel: 'all',
        fetchRows: (signal, updatedSince) =>
            fetchAllPages(
                'customer/fetchCustomers',
                {
                    search: '',
                    filters: [],
                    sortField: 'id',
                    sortDirection: 'asc',
                },
                { signal, updatedSince },
            ),
        normalizeRow: (r) => ({
            id: r.id,
            name: r.name ?? '',
            type: String(r.type || '').toLowerCase() === 'wholesaler' ? 'wholesaler' : 'normal',
            internal_code: r.internal_code ?? null,
            phone: r.phone ?? null,
            mobile: r.mobile ?? null,
            email: r.email ?? null,
            fax: r.fax ?? null,
            gender: r.gender ?? null,
            date_of_birth: r.date_of_birth ?? null,
            cnic: r.cnic ?? null,
            address: r.address ?? null,
            city: r.city ?? null,
            state: r.state ?? null,
            country: r.country ?? null,
            wholesaler_id: r.wholesaler_id ?? null,
            company_name: r.company_name ?? r.companyName ?? null,
            contact_person: r.contact_person ?? null,
            company_contact_number: r.company_contact_number ?? null,
            business_license_number: r.business_license_number ?? null,
            gst_number: r.gst_number ?? null,
            ntn_number: r.ntn_number ?? null,
            strn_number: r.strn_number ?? null,
            ftn_number: r.ftn_number ?? null,
            buyer_type: r.buyer_type ?? null,
            is_active_taxpayer: Number(r.is_active_taxpayer ?? 0),
            advance_tax_exempt: Number(r.advance_tax_exempt ?? 0),
            advance_tax_exemption_ref: r.advance_tax_exemption_ref ?? null,
            wallet_status: r.wallet_status ?? null,
            wallet_balance: r.wallet_balance ?? null,
            credit_limit: r.credit_limit ?? null,
            credit_limit_enabled: Number(r.credit_limit_enabled ?? 0),
            discount_percentage: r.discount_percentage ?? null,
            // Reminder preferences. Deliberately NOT coerced with Number():
            // null means "use the site-wide rule" and must survive as null,
            // whereas Number(null) is 0, which reads as "never send".
            post_sale_reminders: r.post_sale_reminders ?? null,
            dues_reminders: r.dues_reminders ?? null,
            reminder_channels: r.reminder_channels ?? null,
            reminder_opted_out_at: r.reminder_opted_out_at ?? null,
            status: r.status ?? null,
            created_at: r.created_at ?? null,
            updated_at: r.updated_at ?? null,
        }),
    });
}

const PURCHASE_RETENTION_DAYS = 90;

export async function syncPurchases(options = {}) {
    return syncListTable({
        ...options,
        table: 'purchases',
        scope: 'last-90-days',
        retentionLabel: `last-${PURCHASE_RETENTION_DAYS}-days,max-${MAX_RECENT_ROWS}`,
        fetchRows: async (signal, updatedSince) => {
            return fetchAllPages(
                'purchase/fetchPurchaseList',
                {
                    search: '',
                    filters: [],
                    // By id, not by date — SubscriptionRegistry bounds the
                    // manifest the same way, and a backdated document ranks
                    // differently under the two. Disagreeing about *which*
                    // MAX_RECENT_ROWS rows is what makes reconciliation churn.
                    sortField: 'id',
                    sortDirection: 'desc',
                },
                {
                    maxRows: MAX_RECENT_ROWS,
                    signal,
                    updatedSince,
                    keepRow: retentionWindow('date', PURCHASE_RETENTION_DAYS, { updatedSince }),
                },
            );
        },
        normalizeRow: (r) => ({
            id: r.id,
            purchase_order: r.purchase_order ?? r.purchaseOrder ?? null,
            invoice_number: r.invoice_number ?? r.invoiceNumber ?? null,
            supplier_id: r.supplier_id ?? r.supplierId ?? null,
            supplier_name: r.supplier_name ?? r.supplierName ?? r.suppliers_name ?? null,
            status: r.status ?? null,
            payment_status: r.payment_status ?? r.paymentStatus ?? null,
            date: r.date ?? null,
            total: r.total ?? r.grand_total ?? r.final_total ?? null,
            updated_at: r.updated_at ?? null,
        }),
    });
}

const LIST_RETENTION_DAYS = 90;

export async function syncSales(options = {}) {
    return syncListTable({
        ...options,
        table: 'sales',
        scope: 'last-90-days',
        retentionLabel: `last-${LIST_RETENTION_DAYS}-days,max-${MAX_RECENT_ROWS}`,
        fetchRows: async (signal, updatedSince) => {
            return fetchAllPages(
                'sale/fetchSalesList',
                {
                    search: '',
                    filters: [],
                    sortField: 'id',
                    sortDirection: 'desc',
                },
                {
                    maxRows: MAX_RECENT_ROWS,
                    signal,
                    updatedSince,
                    keepRow: retentionWindow('date', LIST_RETENTION_DAYS, { updatedSince }),
                },
            );
        },
        normalizeRow: (r) => ({
            id: r.id,
            sales_order: r.sales_order ?? null,
            status: r.status ?? null,
            payment_status: r.payment_status ?? null,
            date: r.date ?? null,
            branch_id: r.branch_id ?? null,
            payment_branch_id: r.payment_branch_id ?? null,
            customer_id: r.customer_id ?? r.customer?.id ?? null,
            customer_name: r.customer?.name ?? r.customer_name ?? null,
            customer_phone: r.customer?.phone ?? null,
            final_total: r.final_total ?? null,
            paid_amount: r.paid_amount ?? null,
            refunded_amount: r.refunded_amount ?? 0,
            created_at: r.created_at ?? null,
            updated_at: r.updated_at ?? null,
        }),
    });
}

export async function syncTransfers(options = {}) {
    return syncListTable({
        ...options,
        table: 'transfers',
        scope: 'last-90-days',
        retentionLabel: `last-${LIST_RETENTION_DAYS}-days,max-${MAX_RECENT_ROWS}`,
        fetchRows: async (signal, updatedSince) => {
            return fetchAllPages(
                'transfer/list',
                {
                    search: '',
                    filters: [],
                    sortField: 'created_at',
                    sortDirection: 'desc',
                },
                {
                    maxRows: MAX_RECENT_ROWS,
                    signal,
                    updatedSince,
                    keepRow: retentionWindow('created_at', LIST_RETENTION_DAYS, { updatedSince }),
                },
            );
        },
        normalizeRow: (r) => ({
            id: r.id,
            transfer_number: r.transfer_number ?? null,
            status: r.status ?? null,
            from_branch_id: r.from_branch_id ?? null,
            to_branch_id: r.to_branch_id ?? null,
            from_branch_name: r.from_branch?.name ?? r.fromBranch?.name ?? null,
            to_branch_name: r.to_branch?.name ?? r.toBranch?.name ?? null,
            created_at: r.created_at ?? null,
            updated_at: r.updated_at ?? null,
        }),
    });
}

export async function syncGatePasses(options = {}) {
    return syncListTable({
        ...options,
        table: 'gate_passes',
        scope: 'last-90-days',
        retentionLabel: `last-${LIST_RETENTION_DAYS}-days,max-${MAX_RECENT_ROWS}`,
        fetchRows: async (signal, updatedSince) => {
            return fetchAllPages(
                'gatepass/fetchGatePassList',
                {
                    search: '',
                    filters: [],
                    sortField: 'id',
                    sortDirection: 'desc',
                },
                {
                    maxRows: MAX_RECENT_ROWS,
                    signal,
                    updatedSince,
                    keepRow: retentionWindow('date', LIST_RETENTION_DAYS, { updatedSince }),
                },
            );
        },
        normalizeRow: (r) => ({
            id: r.id,
            gatepass_no: r.gatepass_no ?? null,
            status: r.status ?? null,
            date: r.date ?? null,
            sale_id: r.sale_id ?? null,
            transfer_id: r.transfer_id ?? null,
            updated_at: r.updated_at ?? null,
        }),
    });
}

export async function syncLoadForms(options = {}) {
    return syncListTable({
        ...options,
        table: 'load_forms',
        scope: 'last-90-days',
        retentionLabel: `last-${LIST_RETENTION_DAYS}-days,max-${MAX_RECENT_ROWS}`,
        fetchRows: async (signal, updatedSince) => {
            return fetchAllPages(
                'load-form/list',
                {
                    search: '',
                    filters: [],
                    sortField: 'id',
                    sortDirection: 'desc',
                },
                {
                    maxRows: MAX_RECENT_ROWS,
                    signal,
                    updatedSince,
                    keepRow: retentionWindow('date', LIST_RETENTION_DAYS, { updatedSince }),
                },
            );
        },
        // Dexie is type-strict: branch_id and delivery_person_id are indexed and
        // must be Numbers or the offline filters silently match nothing.
        normalizeRow: (r) => ({
            id: r.id,
            load_form_no: r.load_form_no ?? null,
            status: r.status ?? null,
            date: r.date ?? null,
            branch_id: r.branch_id != null ? Number(r.branch_id) : null,
            delivery_person_id: r.delivery_person_id != null ? Number(r.delivery_person_id) : null,
            driver_name: r.driver_name ?? null,
            vehicle_no: r.vehicle_no ?? null,
            loaded_quantity: r.loaded_quantity ?? 0,
            delivered_quantity: r.delivered_quantity ?? 0,
            invoiced_amount: r.invoiced_amount ?? 0,
            collected_amount: r.collected_amount ?? 0,
            updated_at: r.updated_at ?? null,
        }),
    });
}

/**
 * Full directory rather than a recent-window slice.
 *
 * A driver who has not run in ninety days is still someone a gate pass or load
 * form names, and both documents cache only a `delivery_person_id` — this store
 * is where that id resolves to a name offline. Windowing it would leave older
 * documents showing a bare number.
 */
export async function syncDeliveryPersons(options = {}) {
    return syncListTable({
        ...options,
        table: 'delivery_persons',
        scope: 'all',
        retentionLabel: 'all',
        fetchRows: (signal, updatedSince) =>
            fetchAllPages(
                'delivery-person/list',
                {
                    search: '',
                    filters: [],
                    sortField: 'name',
                    sortDirection: 'asc',
                },
                { signal, updatedSince },
            ),
        normalizeRow: (r) => ({
            id: r.id,
            name: r.name ?? null,
            phone: r.phone ?? null,
            cnic: r.cnic ?? null,
            licence_no: r.licence_no ?? null,
            licence_expiry: r.licence_expiry ?? null,
            vehicle_no: r.vehicle_no ?? null,
            // Dexie is type-strict: a string branch_id is invisible to
            // .where('branch_id').equals(5).
            branch_id: r.branch_id != null ? Number(r.branch_id) : null,
            branch_name: r.branch_name ?? null,
            // 1/0 rather than true/false, because the offline DataTable filter
            // compares the stringified row value against the literal '1'.
            is_active: Number(r.is_active ?? 0),
            note: r.note ?? null,
            created_at: r.created_at ?? null,
            updated_at: r.updated_at ?? null,
        }),
    });
}

export async function syncOrderRequests(options = {}) {
    return syncListTable({
        ...options,
        table: 'wholesaler_order_requests',
        scope: 'last-90-days',
        retentionLabel: `last-${LIST_RETENTION_DAYS}-days,max-${MAX_RECENT_ROWS}`,
        fetchRows: async (signal, updatedSince) => {
            return fetchAllPages(
                'wholesaler-order-request/fetchList',
                {
                    search: '',
                    filters: [],
                    sortField: 'created_at',
                    sortDirection: 'desc',
                },
                {
                    maxRows: MAX_RECENT_ROWS,
                    signal,
                    updatedSince,
                    keepRow: retentionWindow('created_at', LIST_RETENTION_DAYS, { updatedSince }),
                },
            );
        },
        normalizeRow: (r) => ({
            id: r.id,
            order_no: r.order_no ?? null,
            status: r.status ?? null,
            wholesaler_id: r.wholesaler_id ?? null,
            wholesaler_name: r.wholesaler_name ?? null,
            sale_id: r.sale_id ?? null,
            total_amount: r.total_amount ?? null,
            created_at: r.created_at ?? null,
            updated_at: r.updated_at ?? null,
        }),
    });
}

export async function syncExpenses(options = {}) {
    return syncListTable({
        ...options,
        table: 'expenses',
        scope: 'last-90-days',
        retentionLabel: `last-${LIST_RETENTION_DAYS}-days,max-${MAX_RECENT_ROWS}`,
        fetchRows: async (signal, updatedSince) => {
            return fetchAllPages(
                'expense/fetchExpenseList',
                {
                    search: '',
                    filters: [],
                    // See syncPurchases: the manifest bounds by id, so the cache
                    // has to pick its window the same way or the two disagree.
                    sortField: 'id',
                    sortDirection: 'desc',
                },
                {
                    maxRows: MAX_RECENT_ROWS,
                    signal,
                    updatedSince,
                    keepRow: retentionWindow('date', LIST_RETENTION_DAYS, { updatedSince }),
                },
            );
        },
        normalizeRow: (r) => ({
            id: r.id,
            expense_number: r.expense_number ?? null,
            title: r.title ?? null,
            description: r.description ?? null,
            amount: r.amount ?? null,
            date: r.date ?? null,
            payment_method: r.payment_method ?? null,
            branch_id: r.branch_id ?? null,
            category_id: r.category_id ?? null,
            created_at: r.created_at ?? null,
            updated_at: r.updated_at ?? null,
        }),
    });
}

export async function syncAccounts(options = {}) {
    return syncListTable({
        ...options,
        table: 'accounts',
        scope: 'all',
        retentionLabel: 'all',
        fetchRows: (signal, updatedSince) =>
            fetchAllPages(
                'account/fetchAccountList',
                {
                    search: '',
                    sortBy: 'id',
                    sortOrder: 'asc',
                },
                { signal, updatedSince },
            ),
        normalizeRow: (r) => {
            const cachedAt = new Date().toISOString();
            return {
                id: r.id,
                account_title: r.account_title ?? null,
                account_number: r.account_number ?? null,
                bank_name: r.bank_name ?? null,
                country: r.country ?? null,
                status: r.status ?? null,
                use_for_sales: r.use_for_sales ?? false,
                use_for_purchases: r.use_for_purchases ?? false,
                use_for_refunds: r.use_for_refunds ?? false,
                use_for_expenses: r.use_for_expenses ?? false,
                current_balance: r.current_balance ?? null,
                updated_at: r.updated_at ?? null,
                _cached_at: cachedAt,
            };
        },
    });
}

/**
 * Orchestrate every list sync. One failure does not cancel the others.
 *
 * This is the whole repair path for a session that missed realtime events —
 * login, offline→online and a tab returning from the background all land here,
 * so a domain the app can read offline and that is *not* listed below simply
 * never converges after an outage.
 *
 * The branch-stock TTL bust runs ahead of the listStoresSync gate on purpose:
 * branch_products is POS stock rather than a list store, and it goes stale on
 * exactly this boundary whether or not list caching is enabled.
 *
 * Each table decides for itself whether this run is a delta or a full reconcile
 * (see syncListTable). `mode: 'full'` forces the reconcile everywhere, which is
 * what the Settings screen's manual refresh is for.
 *
 * The task list is ordered master directories first, document streams second.
 * Documents cache only a foreign key — a load form holds `delivery_person_id`, a
 * sale holds `customer_id` — so a run that is interrupted, rate-limited or cut
 * short by a lost connection leaves the tables those ids resolve against already
 * seeded, rather than a list of screens showing bare numbers.
 *
 * At most SYNC_CONCURRENCY tables run at once, and a table inside its retry
 * window is skipped with `reason: 'backoff'` rather than re-attempted on every
 * trigger. `force: true` ignores that window, which is what the manual refresh
 * needs: an operator asking for a refresh is new information.
 */
export async function runOfflineListSync({ reason = 'manual', mode = 'auto', force = false } = {}) {
    if (!canMutateOnline()) {
        return { ok: false, reason: 'offline' };
    }

    const staleStockBranchIds = await invalidateBranchStockCaches();
    if (!isOfflineFlagEnabled('listStoresSync')) {
        return { ok: false, reason: 'flag_disabled', staleStockBranchIds };
    }

    const options = { mode };
    const tasks = [
        ['branches', () => syncBranches(options)],
        ['suppliers', () => syncSuppliers(options)],
        ['customers', () => syncCustomers(options)],
        ['automotives', () => syncAutomotives(options)],
        ['delivery_persons', () => syncDeliveryPersons(options)],
        ['accounts', () => syncAccounts(options)],
        ['purchases', () => syncPurchases(options)],
        ['sales', () => syncSales(options)],
        ['transfers', () => syncTransfers(options)],
        ['gate_passes', () => syncGatePasses(options)],
        ['load_forms', () => syncLoadForms(options)],
        ['wholesaler_order_requests', () => syncOrderRequests(options)],
        ['expenses', () => syncExpenses(options)],
    ];

    // Thunks rather than started promises: the entries above used to be *calls*,
    // so all thirteen were already in flight before anything could bound them.
    const results = await runDueTasks(tasks, { honourBackoff: !force && mode !== 'full' });

    const summary = results.map((r, i) => ({
        table: tasks[i][0],
        status: r.status,
        value: r.status === 'fulfilled' ? r.value : String(r.reason),
    }));

    scheduleTableRetries();

    // A table the caller is not entitled to read is a correct outcome, not a
    // partial failure: reporting it as one turns every cashier's login into a
    // console warning, and makes the change feed treat a healthy reseed as
    // failed (see runChangeFeedSync).
    // `backoff` joins `not_permitted` as a correct outcome rather than a partial
    // failure: the table is deliberately waiting out its retry window, and the
    // timer armed above will come back for it.
    const succeeded = (item) =>
        item.status === 'fulfilled' && (item.value?.ok || item.value?.reason === 'not_permitted' || item.value?.reason === 'backoff');

    const ok = summary.every(succeeded);
    if (!ok) {
        console.warn(
            '[offlineSync] run complete with failures',
            reason,
            summary.filter((item) => !succeeded(item)),
        );
    }
    return { ok, reason: ok ? undefined : 'partial_failure', results: summary, staleStockBranchIds };
}

/**
 * Arm one timer for the earliest table due a retry.
 *
 * One timer for the whole service rather than one per table: the retry re-enters
 * `runOfflineListSync`, which re-evaluates every table's window anyway, so a
 * second timer could only duplicate work. The run it schedules re-arms this from
 * its own results, and a table that succeeds clears `next_attempt_at` and drops
 * out — so the ladder terminates on success and, via MAX_TABLE_FAILURES, on
 * persistent failure too.
 */
async function scheduleTableRetries() {
    if (retryTimerId) {
        clearTimeout(retryTimerId);
        retryTimerId = null;
    }
    try {
        if (!offlineDB.offline_sync_meta) return;
        const rows = await offlineDB.offline_sync_meta.toArray();
        let earliest = Infinity;
        for (const row of rows) {
            const at = row?.next_attempt_at ? Date.parse(row.next_attempt_at) : NaN;
            if (!Number.isNaN(at) && at < earliest) earliest = at;
        }
        if (!Number.isFinite(earliest)) return;

        retryTimerId = setTimeout(
            () => {
                retryTimerId = null;
                // Offline is not a retry opportunity; the reconnect path will
                // run the whole convergence anyway.
                if (!canMutateOnline()) return;
                runOfflineListSync({ reason: 'table_retry' }).catch((e) => console.warn('[offlineSync] table retry failed', e));
            },
            Math.max(1000, earliest - Date.now()),
        );
    } catch (e) {
        console.warn('[offlineSync] could not schedule table retries', e);
    }
}

/**
 * Every list runner, keyed by the store it fills.
 *
 * One map rather than the two hand-maintained copies this replaced: the same
 * thirteen names were also written out inside `scheduleAuthoritativeSync`, so a
 * domain added to one and forgotten in the other converged on a live event but
 * not on a replayed one, or the reverse. Declared after the fan-out so the task
 * list there stays the literal, readable statement of seed order.
 */
export const SYNC_RUNNERS = {
    branches: syncBranches,
    suppliers: syncSuppliers,
    customers: syncCustomers,
    automotives: syncAutomotives,
    delivery_persons: syncDeliveryPersons,
    accounts: syncAccounts,
    purchases: syncPurchases,
    sales: syncSales,
    transfers: syncTransfers,
    gate_passes: syncGatePasses,
    load_forms: syncLoadForms,
    wholesaler_order_requests: syncOrderRequests,
    expenses: syncExpenses,
};

/**
 * How long a table's offline window may stand after a successful sync before the
 * next visit to its screen refreshes it.
 *
 * Short, because what a visit costs is one delta — `updated_at >= watermark`,
 * which on a table nothing touched is a single request answering zero rows. It
 * exists to stop tab-flipping and a remount storm from turning one screen into
 * ten requests, not to bound staleness; Reverb and the change feed do that.
 */
const VISIT_SYNC_TTL_MS = 5 * 60_000;

/**
 * Whether `table` is still inside the retry window a previous failure earned it.
 *
 * The ladder itself has always been written by `syncListTable`, but only the
 * orchestrator's own fan-out ever read it. Every other trigger — a visit, and
 * above all `scheduleAuthoritativeSync`, which fires on each invalidate — went
 * straight past. On a shop where one list endpoint is answering 503 that turned
 * a broken table into a request per realtime event, which is precisely the loop
 * the ladder exists to stop. So the check lives with the *triggers* rather than
 * inside the runner: a caller who has genuinely new information (an operator
 * pressing refresh) is still entitled to a sync it would otherwise be denied.
 */
export async function isTableInBackoff(table, scope = SYNC_SCOPES[table] ?? 'all') {
    const meta = await getSyncMeta(table, scope);
    const retryAt = meta?.next_attempt_at ? Date.parse(meta.next_attempt_at) : NaN;
    return !Number.isNaN(retryAt) && retryAt > Date.now();
}

/**
 * Bring one table's offline window up to date because the screen that reads it
 * was just opened.
 *
 * This is the replacement for seeding all thirteen tables on every boot. That
 * fan-out treated "the user reloaded the page" as evidence about every domain in
 * the application, when it is only evidence about the one they are looking at —
 * so a cashier opening the till paid for a load-form scan, an expense scan and
 * eleven others, on every refresh, forever.
 *
 * Three reasons to do nothing, in the order they are cheapest to establish:
 * the table has no offline window at all; the session may not read it; it was
 * synced moments ago. The fourth — the table is inside its retry window after a
 * failure — is the one that matters for a broken endpoint: a 503 must not become
 * a request per navigation, and an operator clicking between two screens is not
 * new information about whether the server has recovered. `runOfflineListSync`'s
 * `force` path (the Settings refresh button) is the way to say that it is.
 *
 * Deliberately not awaited by its callers. A visit refreshes the cache the
 * *next* offline read will use; the screen in front of the operator is already
 * being served by its own request.
 */
export async function ensureTableSynced(table, { ttlMs = VISIT_SYNC_TTL_MS, force = false } = {}) {
    const run = SYNC_RUNNERS[table];
    if (!run) return { ok: false, reason: 'no_runner' };
    if (!canMutateOnline()) return { ok: false, reason: 'offline' };
    if (!isOfflineFlagEnabled('listStoresSync')) return { ok: false, reason: 'flag_disabled' };
    if (!canSyncTable(table)) return { ok: false, reason: 'not_permitted' };

    if (!force) {
        const scope = SYNC_SCOPES[table] ?? 'all';
        if (await isTableInBackoff(table, scope)) return { ok: false, reason: 'backoff' };
        // No successful sync yet is the case a visit exists to fix — seed it.
        const meta = await getSyncMeta(table, scope);
        const syncedAt = meta?.last_success_at ? Date.parse(meta.last_success_at) : NaN;
        if (!Number.isNaN(syncedAt) && Date.now() - syncedAt < ttlMs) {
            return { ok: false, reason: 'fresh' };
        }
    }

    return run({ mode: 'auto' });
}

export async function getSyncMeta(table, scope = 'all') {
    if (!offlineDB.offline_sync_meta) return null;
    return offlineDB.offline_sync_meta.get(metaKey(table, scope));
}

export async function cancelOfflineListSyncs() {
    syncGeneration += 1;
    if (retryTimerId) {
        clearTimeout(retryTimerId);
        retryTimerId = null;
    }
    for (const controller of activeControllers) controller.abort();
    const runsToDrain = [...activeRuns];
    // Do not let a subsequent user's sync deduplicate against old-user work.
    inflight.clear();
    if (runsToDrain.length) {
        await Promise.allSettled(runsToDrain);
    }
}

export default {
    canSyncTable,
    ensureTableSynced,
    syncListTable,
    syncBranches,
    syncSuppliers,
    syncCustomers,
    syncAutomotives,
    syncPurchases,
    syncSales,
    syncTransfers,
    syncGatePasses,
    syncLoadForms,
    syncDeliveryPersons,
    syncOrderRequests,
    syncExpenses,
    syncAccounts,
    runOfflineListSync,
    getSyncMeta,
    cancelOfflineListSyncs,
};
